import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Grid,
  Chip,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Check,
  Star,
  ArrowForward,
  Security,
  Schedule,
  Assignment,
} from '@mui/icons-material';
import PaymentForm from '../components/PaymentForm';
import { useAuth } from '../contexts/AuthContext';

interface ServiceOption {
  id: 'fmla' | 'fmla-std';
  name: string;
  description: string;
  price: number;
  features: string[];
  popular?: boolean;
}

const ServiceSelectionPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedService, setSelectedService] = useState<ServiceOption | null>(null);
  const [showPayment, setShowPayment] = useState(false);

  const services: ServiceOption[] = [
    {
      id: 'fmla',
      name: 'FMLA Certification',
      description: 'Complete FMLA medical certification through secure video consultation',
      price: 100,
      features: [
        'Licensed healthcare provider consultation',
        'PHQ-9 & GAD-7 standardized assessments',
        'Comprehensive biopsychosocial evaluation',
        'DOL-compliant FMLA certification forms',
        'Secure document upload and storage',
        '24-48 hour turnaround time'
      ]
    },
    {
      id: 'fmla-std',
      name: 'FMLA + Short-Term Disability',
      description: 'Combined FMLA and STD certification bundle for comprehensive coverage',
      price: 200,
      popular: true,
      features: [
        'Everything in FMLA Certification',
        'Short-term disability assessment',
        'Extended functional capacity evaluation',
        'Multiple certification forms included',
        'Priority scheduling available',
        'Follow-up consultation included'
      ]
    }
  ];

  const handleServiceSelect = (service: ServiceOption) => {
    if (!user) {
      navigate('/login');
      return;
    }
    setSelectedService(service);
    setShowPayment(true);
  };

  const handlePaymentSuccess = () => {
    // Payment successful, user will be redirected by Stripe
    // The backend webhook will create/update the user account
  };

  const handleBackToServices = () => {
    setShowPayment(false);
    setSelectedService(null);
  };

  if (showPayment && selectedService && user) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Button onClick={handleBackToServices} sx={{ mr: 2 }}>
            ← Back to Services
          </Button>
          <div>
            <Typography variant="h4" component="h1">
              Complete Your Purchase
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {selectedService.name} - ${selectedService.price}
            </Typography>
          </div>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Selected Service
                </Typography>
                <Typography variant="h5" color="primary" gutterBottom>
                  {selectedService.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {selectedService.description}
                </Typography>
                
                <List dense>
                  {selectedService.features.map((feature, index) => (
                    <ListItem key={index} sx={{ pl: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Check color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={feature} 
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <PaymentForm
              amount={`$${selectedService.price}`}
              serviceName={selectedService.name}
              serviceType={selectedService.id}
              onPaymentSuccess={handlePaymentSuccess}
              onPaymentCancel={handleBackToServices}
              userData={{
                firstName: user.name.split(' ')[0] || '',
                lastName: user.name.split(' ').slice(1).join(' ') || '',
                email: user.email,
                employmentState: user.employmentState || '',
              }}
            />
          </Grid>
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Choose Your FMLA Service
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Licensed healthcare providers • Secure video consultations • HIPAA compliant
        </Typography>
      </Box>

      {user && (
        <Paper sx={{ p: 3, mb: 4, background: 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Security color="primary" />
            </Grid>
            <Grid item xs>
              <Typography variant="h6">
                Welcome, {user.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Employment State: {user.employmentState} • Email: {user.email}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {!user && (
        <Alert severity="info" sx={{ mb: 4 }}>
          <Button onClick={() => navigate('/login')} sx={{ ml: 1 }} variant="text">
            Log in
          </Button>
          or create an account to get started after selecting a service.
        </Alert>
      )}

      {/* Service Options */}
      <Grid container spacing={3}>
        {services.map((service) => (
          <Grid item xs={12} md={6} key={service.id}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: service.popular ? '2px solid #1976d2' : '1px solid #e0e0e0',
                position: 'relative'
              }}
            >
              {service.popular && (
                <Chip 
                  label="Most Popular" 
                  color="primary"
                  icon={<Star />}
                  sx={{ 
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    zIndex: 1
                  }}
                />
              )}
              
              <CardContent sx={{ flexGrow: 1, p: 4 }}>
                <Typography variant="h5" gutterBottom>
                  {service.name}
                </Typography>
                
                <Typography variant="h4" color="primary" gutterBottom>
                  ${service.price}
                </Typography>
                
                <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                  {service.description}
                </Typography>

                <List dense>
                  {service.features.map((feature, index) => (
                    <ListItem key={index} sx={{ pl: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Check color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={feature} 
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
              
              <CardContent sx={{ pt: 0 }}>
                <Button
                  variant={service.popular ? "contained" : "outlined"}
                  fullWidth
                  size="large"
                  endIcon={<ArrowForward />}
                  onClick={() => handleServiceSelect(service)}
                  sx={{ py: 1.5 }}
                >
                  Select {service.name}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Trust Indicators */}
      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          Why Choose FMLA Doctor?
        </Typography>
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={4}>
            <Security fontSize="large" color="primary" />
            <Typography variant="subtitle1" gutterBottom>
              HIPAA Compliant
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your medical information is protected with enterprise-grade security
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Schedule fontSize="large" color="primary" />
            <Typography variant="subtitle1" gutterBottom>
              Fast Turnaround
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Get your FMLA certification within 24-48 hours
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Assignment fontSize="large" color="primary" />
            <Typography variant="subtitle1" gutterBottom>
              Licensed Providers
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All consultations are conducted by licensed healthcare professionals
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default ServiceSelectionPage;